import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, ArrowLeftRight, CalendarPlus } from "lucide-react";
import { BRAND, errMsg } from "./constants";

function RequestRow({ rq, vacant, onApprove, onReject }) {
  const [bed, setBed] = useState(vacant.some((b) => b.id === rq.requested_bed_id) ? rq.requested_bed_id : "");
  useEffect(() => { if (!bed && vacant.some((b) => b.id === rq.requested_bed_id)) setBed(rq.requested_bed_id); }, [vacant]); // eslint-disable-line
  const [amt, setAmt] = useState("");
  const Icon = rq.kind === "bed_change" ? ArrowLeftRight : CalendarPlus;
  return (
    <div data-testid={`request-row-${rq.id}`} className="py-3 border-t text-sm" style={{ borderColor: BRAND.line }}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-bold inline-flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" style={{ color: BRAND.orange }} /> {rq.guest_name}
            <span className="chip text-[10px]">{rq.kind === "bed_change" ? "BED CHANGE" : rq.plan.toUpperCase()}</span>
            {rq.source === "flatmate" && <span data-testid={`request-flatmate-${rq.id}`} className="chip text-[10px]" style={{ color: BRAND.orange }}>FLATMATE · wants {rq.requested_bed_label}</span>}</div>
          <div className="text-xs" style={{ color: BRAND.muted }}>{rq.check_in}{rq.check_out ? ` → ${rq.check_out}` : ""} · {rq.guest_phone || "no phone"}{rq.note ? ` · "${rq.note}"` : ""}</div>
        </div>
        <span data-testid={`request-status-${rq.id}`} className="text-[10px] font-bold uppercase" style={{ color: rq.status === "pending" ? BRAND.orange : rq.status === "approved" ? BRAND.green : "#DC2626" }}>
          {rq.status}{rq.assigned_bed_label ? ` · ${rq.assigned_bed_label}` : ""}
        </span>
      </div>
      {rq.status === "pending" && (
        <div className="mt-2 flex flex-wrap gap-2">
          <select data-testid={`request-bed-${rq.id}`} className="rounded-xl border-2 px-2 py-1.5 text-xs" value={bed} onChange={(e) => setBed(e.target.value)}>
            <option value="">Pick vacant bed…</option>
            {vacant.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
          </select>
          {rq.kind === "booking" && <input type="number" min={0} placeholder="Amount ₹" data-testid={`request-amount-${rq.id}`} className="rounded-xl border-2 px-2 py-1.5 text-xs w-28" value={amt} onChange={(e) => setAmt(e.target.value)} />}
          <button disabled={!bed} data-testid={`request-approve-${rq.id}`} onClick={() => onApprove(rq, bed, +amt || 0)} className="rounded-full px-3 py-1.5 text-xs font-bold text-white inline-flex items-center gap-1 disabled:opacity-50" style={{ background: BRAND.green }}><Check className="w-3 h-3" /> Approve</button>
          <button data-testid={`request-reject-${rq.id}`} onClick={() => onReject(rq)} className="rounded-full px-3 py-1.5 text-xs font-bold border-2 inline-flex items-center gap-1" style={{ borderColor: "#DC2626", color: "#DC2626" }}><X className="w-3 h-3" /> Decline</button>
        </div>
      )}
    </div>
  );
}

export function RequestsPanel({ pid, authFetch, API }) {
  const [items, setItems] = useState([]);
  const [vacant, setVacant] = useState([]);
  const load = useCallback(() => {
    authFetch({ url: `${API}/homestay/properties/${pid}/requests` }).then((r) => setItems(r.data.items)).catch(() => {});
    authFetch({ url: `${API}/homestay/properties/${pid}/board` }).then((r) => setVacant(r.data.rooms.flatMap((rm) => rm.beds).filter((b) => b.status === "vacant"))).catch(() => {});
  }, [pid, authFetch, API]);
  useEffect(() => { load(); }, [load]);

  const approve = async (rq, bed_id, amount_inr) => {
    try { const r = await authFetch({ method: "post", url: `${API}/homestay/requests/${rq.id}/approve`, data: { bed_id, amount_inr } }); toast.success(`Assigned ${r.data.bed_label}`); load(); }
    catch (e) { toast.error(errMsg(e, "Could not approve")); }
  };
  const reject = async (rq) => {
    const reason = window.prompt("Reason (sent to guest)", "No beds available for those dates");
    if (reason === null) return;
    try { await authFetch({ method: "post", url: `${API}/homestay/requests/${rq.id}/reject`, data: { reason } }); toast.success("Declined"); load(); }
    catch (e) { toast.error(errMsg(e, "Could not decline")); }
  };

  return (
    <div data-testid="requests-panel" className="rounded-2xl border-2 bg-white p-4" style={{ borderColor: BRAND.line }}>
      <div className="text-sm font-bold" style={{ color: BRAND.green }}>Guest bed requests</div>
      <p className="text-xs" style={{ color: BRAND.muted }}>New bookings and bed-change asks from your public page. Approving reserves the bed and notifies the guest.</p>
      {items.length === 0 && <div className="text-xs mt-3" style={{ color: BRAND.muted }} data-testid="requests-empty">No requests yet. Share your public page link with guests.</div>}
      <div className="mt-2 max-h-[28rem] overflow-y-auto">
        {items.map((rq) => <RequestRow key={rq.id} rq={rq} vacant={vacant} onApprove={approve} onReject={reject} />)}
      </div>
    </div>
  );
}
